import { captureException } from '@sentry/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addEdge,
  ConnectionLineType,
  MarkerType,
  Panel,
  ReactFlow,
  updateEdge,
  useNodesState,
  useEdgesState,
  Background,
  MiniMap,
} from 'reactflow'

import type { ReactElement } from 'react'
import type { Edge, Connection } from 'reactflow'
import type { ITask } from '~/types/app-setup'

import CreateModal from './CreateModal'

import Button from '~/components/Buttons/Button'
import Layout from '~/components/Layouts/Layout'
import Modal from '~/components/Modal'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import Resizer from '~/components/ReactFlow/Resizer'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppSetupTaksQuery, useDeleteTaskMutation, useUpdateTaskMutation } from '~/queries/application-setup'
import { edgeTypes, getLayoutedElements, nodeTypes } from '~/utils/react-flow'
import { displayToast } from '~/utils/toast'

import './styles.scss'
import 'reactflow/dist/style.css'

export const MODAL_ID = 'create-task-modal'

const AppsSetup = (): ReactElement => {
  /** Local state */

  const isActionAuthorized = useIsUserAuthorized([AUTHORIZATIONS.APP_SETUP.TASK.WRITE])

  const edgeUpdateSuccessful = useRef(true)

  const [isCreateModalOpen, setCreateModalOpen] = useState(false)

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const [nodes, setNodes, onNodesChange] = useNodesState<ITask>([])

  const [edges, setEdges, onEdgesChange] = useEdgesState<ITask>([])

  const [selectedTask, setSelectedTask] = useState<ITask>()

  /** Queries & Mutations */

  const { data, isLoading } = useAppSetupTaksQuery()

  const { mutateAsync: deleteTaskMutation } = useDeleteTaskMutation()

  const { mutate: updateTaskMutation } = useUpdateTaskMutation()

  /** Effects */

  useDocumentTitle('Apps Setup')

  useEffect(() => {
    if (data) {
      const initialNodes = []
      const initialEdges = []

      for (const task of data) {
        initialNodes.push({
          data: {
            ...task,
            isEditable: isActionAuthorized,
            onDelete: handleDeleteTask,
            onEdit: handleEditTask,
          },
          id: task.id,
          position: { x: 0, y: 0 },
          type: 'app-setup-card',
        })

        if (task.previous_task_ids && task.previous_task_ids.length) {
          for (const previousStepId of task.previous_task_ids) {
            initialEdges.push({
              id: `${previousStepId}-${task.id}`,
              markerEnd: {
                type: MarkerType.ArrowClosed,
              },
              source: previousStepId,
              target: task.id,
              type: isActionAuthorized ? 'buttonedge' : 'bezier',
            })
          }
        }
      }

      const { edges: layoutedEdges, nodes: layoutedNodes } = getLayoutedElements(initialNodes, initialEdges)

      setNodes(layoutedNodes)
      setEdges(layoutedEdges)
    }
  }, [data, isActionAuthorized, setNodes, setEdges])

  /** Handlers */

  const handleCreateModalState = (isOpen: boolean) => () => setCreateModalOpen(isOpen)

  const handleDeleteModalState = (isOpen: boolean) => () => setDeleteModalOpen(isOpen)

  const handleCreateOrEditTaskSuccess = (task: ITask) => {
    let updatedNodes

    if (selectedTask) {
      updatedNodes = nodes.map((n) => {
        if (n.id === task.id) {
          return {
            ...n,
            data: {
              ...n.data,
              ...task,
            },
          }
        }

        return n
      })
    } else {
      const n = {
        data: {
          ...task,
          onDelete: handleDeleteTask,
          onEdit: handleEditTask,
        },
        id: task.id,
        position: { x: 0, y: 0 },
        type: 'app-setup-card',
      }

      updatedNodes = nodes.concat(n)
    }

    setNodes(updatedNodes)
    setCreateModalOpen(false)

    displayToast(`Task ${selectedTask ? 'updated' : 'created'}`, 'success')

    setSelectedTask(undefined)
  }

  const handleEditTask = (task: ITask) => {
    setSelectedTask(task)
    setCreateModalOpen(true)
  }

  const handleDeleteTask = (task: ITask) => {
    setSelectedTask(task)
    setDeleteModalOpen(true)
  }

  const handleDeleteTaskConfirm = async () => {
    if (isActionAuthorized && selectedTask) {
      try {
        await deleteTaskMutation(selectedTask.id)
        setDeleteModalOpen(false)

        const updatedNodes = nodes.filter((n) => n.id !== selectedTask.id)

        setNodes(updatedNodes)
        setSelectedTask(undefined)
        displayToast('Task deleted', 'success')
      } catch {
        displayToast('Task could not be deleted!', 'error')
      }
    }
  }

  const handleConnect = useCallback(
    (edgeParams: Edge | Connection) => {
      const { source, target } = edgeParams
      const targetNode = nodes.find(({ id }) => id === target)

      if (isActionAuthorized && target && targetNode && source) {
        try {
          updateTaskMutation({
            payload: {
              ...targetNode.data,
              previous_task_ids: targetNode.data.previous_task_ids
                ? [...new Set([...targetNode.data.previous_task_ids, source])]
                : [source],
            },
          })
          setEdges((edg) =>
            addEdge(
              {
                ...edgeParams,
                type: 'app-setup-button-edge',
              },
              edg
            )
          )
        } catch (e) {
          displayToast('Tasks could not be linked!', 'error')
          captureException(e)
        }
      }
    },
    [isActionAuthorized, nodes, setEdges, updateTaskMutation]
  )

  const handleEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false
  }, [])

  const handleEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      // NOTE: set new connection
      const { source, target } = newConnection
      const targetNode = nodes.find(({ id }) => id === target)

      if (isActionAuthorized && target && targetNode && source) {
        try {
          updateTaskMutation({
            payload: {
              ...targetNode.data,
              previous_task_ids: targetNode.data.previous_task_ids
                ? [...new Set([...targetNode.data.previous_task_ids, source])]
                : [source],
            },
          })
          setEdges((eds) => updateEdge(oldEdge, newConnection, eds))
        } catch (e) {
          displayToast('Tasks could not be linked!', 'error')
          captureException(e)
        }
      }

      edgeUpdateSuccessful.current = true
    },
    [isActionAuthorized, nodes, setEdges, updateTaskMutation]
  )

  const handleEdgeUpdateEnd = useCallback(
    (_: MouseEvent | TouchEvent, oldEdge: Edge) => {
      // NOTE: remove old connection
      if (!edgeUpdateSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== oldEdge.id))
      }

      const { source, target } = oldEdge
      const targetNode = nodes.find(({ id }) => id === target)

      if (isActionAuthorized && target && targetNode) {
        try {
          updateTaskMutation({
            payload: {
              ...targetNode.data,
              previous_task_ids: targetNode.data.previous_task_ids?.filter((id: string) => id !== source) || [],
            },
          })
        } catch (e) {
          displayToast('Tasks could not be unlinked!', 'error')
          captureException(e)
        }
      }

      edgeUpdateSuccessful.current = true
    },
    [isActionAuthorized, nodes, setEdges, updateTaskMutation]
  )

  const authorizedActions = useMemo(() => {
    return isActionAuthorized
      ? {
          onConnect: handleConnect,
          onEdgeUpdate: handleEdgeUpdate,
          onEdgeUpdateEnd: handleEdgeUpdateEnd,
          onEdgeUpdateStart: handleEdgeUpdateStart,
          onEdgesChange: onEdgesChange,
          onNodesChange: onNodesChange,
        }
      : {}
  }, [
    handleConnect,
    handleEdgeUpdate,
    handleEdgeUpdateEnd,
    handleEdgeUpdateStart,
    onEdgesChange,
    onNodesChange,
    isActionAuthorized,
  ])

  return (
    <Layout className="apps-setup-view" withPadding>
      <ViewTitle className="layout-view-title">Apps Setup</ViewTitle>
      {isLoading ? (
        <div className="loader-container">
          <MwMLoader category="item" />
        </div>
      ) : (
        <>
          <ReactFlow
            connectionLineType={ConnectionLineType.SmoothStep}
            edgeTypes={edgeTypes}
            edges={edges}
            fitView
            maxZoom={1}
            nodeTypes={nodeTypes}
            nodes={nodes}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
            {...authorizedActions}
          >
            <Background />
            <MiniMap />
            <Resizer />
            {isActionAuthorized ? (
              <Panel className="top-left-panel" position="top-left">
                <Button className="add-task-button" onClick={handleCreateModalState(true)}>
                  + task
                </Button>
              </Panel>
            ) : null}
          </ReactFlow>
          <Modal id={MODAL_ID} isOpen={isCreateModalOpen} onCloseModal={handleCreateModalState(false)}>
            <CreateModal
              onClose={handleCreateModalState(false)}
              onSuccess={handleCreateOrEditTaskSuccess}
              task={selectedTask}
            />
          </Modal>
          <ModalDialog
            isOpen={isDeleteModalOpen}
            mainText="Do you really want to delete this task?"
            onClose={handleDeleteModalState(false)}
            onConfirm={handleDeleteTaskConfirm}
          />
        </>
      )}
    </Layout>
  )
}

export default AppsSetup
